import { useMemo } from "react";
import { formatAmount, invoicesRequestedTokenMap } from "@utils";
import {
  buildRequests,
  calcMaxRequestableAmount,
  buildTokensForRow,
} from "../../PaymentRequestDrawers/utils";
const isErrorStatus = (status) =>
  ["amountTooSmall", "noContactDetails", "noPhone", "noEmail"].includes(status);

export function useDerivedData({
  customerData,
  invoicesRequested,
  isCustom,
  isSpecific,
  watchedCustomAmount,
  smsEnabled,
  emailEnabled,
  previewCustomerIdx,
  rows,
  isSingleCustomer,
}) {
  const preparedRows = useMemo(
    () =>
      customerData?.length
        ? buildRequests(customerData, invoicesRequested, 0.3, watchedCustomAmount)
        : [],
    [customerData, invoicesRequested, watchedCustomAmount],
  );

  const grossAmount = useMemo(
    () =>
      preparedRows.length
        ? preparedRows.reduce((s, r) => s + r.minimumAmount, 0)
        : 0,
    [preparedRows],
  );

  const sendableAmount = useMemo(
    () => calcMaxRequestableAmount(preparedRows, smsEnabled, emailEnabled),
    [preparedRows, smsEnabled, emailEnabled],
  );

  const previewTokens = useMemo(() => {
    const row = preparedRows[previewCustomerIdx];
    if (!row) return undefined;
    // Custom and specific-invoices sends carry a fixed figure (the latter the selected invoices'
    // total, seeded onto customAmount); every other mode derives its figure from the row's buckets.
    const amount = isCustom || isSpecific
      ? formatAmount(watchedCustomAmount || 0)
      : formatAmount(invoicesRequested === "None" ? row.balance : row.minimumAmount);
    return buildTokensForRow(
      row,
      amount,
      isCustom || isSpecific
        ? null
        : (invoicesRequestedTokenMap[invoicesRequested] ?? invoicesRequested),
    );
  }, [
    preparedRows,
    previewCustomerIdx,
    invoicesRequested,
    isCustom,
    isSpecific,
    watchedCustomAmount,
  ]);

  const isRelevantError = isSingleCustomer
    ? (status) => status === "amountTooSmall"
    : isErrorStatus;

  const sendableCount = useMemo(
    () =>
      rows?.length ? rows.filter((r) => !isRelevantError(r.status)).length : 0,
    [rows, isRelevantError],
  );

  const unsendableCount = useMemo(
    () =>
      rows?.length ? rows.filter((r) => isRelevantError(r.status)).length : 0,
    [rows, isRelevantError],
  );

  const partialCount = useMemo(
    () => {
      if (isSingleCustomer) return 0;
      if (!smsEnabled || !emailEnabled || !rows?.length) return 0;
      return rows.filter(
        (r) => r.status === "ready" && r.phoneError !== r.emailError,
      ).length;
    },
    [rows, smsEnabled, emailEnabled, isSingleCustomer],
  );

  return {
    preparedRows,
    grossAmount,
    sendableAmount,
    previewTokens,
    sendableCount,
    unsendableCount,
    partialCount,
  };
}
